import DatePicker, { CalendarContainer } from "react-datepicker";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useEffect, useCallback, memo, useRef } from "react";

import "./FilterRow.scss";
import DropdownSelect from "../dropdown-select/DropdownSelect";
import {
  currentMonthRangeSelect,
  currentQuarterRangeSelect,
  currentYearRangeSelect,
  lastYearSpanRangeSelect,
  previousMonthRangeSelect,
  previousQuarterRangeSelect,
  previousWeekRangeSelect,
  previousYearRangeSelect,
  thisWeekRangeSelect,
} from "../../service/utils/functions";
import DropdownMultiselect from "../dropdown-multiselect/DropdownMultiselect";
import * as Storage from "../../service/storage";
import {
  CustomerDataEntry,
  JobStatusDataEntry,
  SiteDataEntry,
  WasteTypeDataEntry,
} from "../../service/utils/interfaces";

interface FilterRowProps {
  width: number;
  isTippingUser?: boolean;
  customerData: CustomerDataEntry[];
  onChangeCustomerData: (update: CustomerDataEntry) => void;
  siteData?: SiteDataEntry[];
  onChangeSiteData?: (update: SiteDataEntry[]) => void;
  wasteTypeData?: WasteTypeDataEntry[];
  onChangeWasteTypeData?: (update: WasteTypeDataEntry[]) => void;
  statusData?: JobStatusDataEntry[];
  onChangeStatusData?: (update: JobStatusDataEntry[]) => void;
  invoiceTypeData?: any[];
  onChangeInvoiceTypeData?: (update: any) => void;
  documentTypeData?: any[];
  onChangeDocumentTypeData?: (update: any) => void;
  displayDateRange?: boolean;
  startDate?: Date | null;
  endDate?: Date | null;
  onChangeTimeRange?: (update: [Date | null, Date | null]) => void;
  noSideButton?: boolean;
  darkMode: boolean;
}

const FilterRow = memo((props: FilterRowProps) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(4);
  const [dateRange, setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >(() => {
    const start = props.startDate ?? undefined;
    const end = props.endDate ?? undefined;
    return [start, end];
  });
  const [localStartDate, localEndDate] = dateRange;

  const [siteResetting, setSiteResetting] = useState(false);
  const [wasteTypeResetting, setWasteTypeResetting] = useState(false);
  const [statusResetting, setStatusResetting] = useState(false);
  const [invoiceTypeResetting, setInvoiceTypeResetting] = useState(false);
  const [documentTypeResetting, setDocumentTypeResetting] = useState(false);

  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [isClosingDatepicker, setIsClosingDatepicker] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [initialCustomer, setInitialCustomer] = useState<CustomerDataEntry>();
  const [selectedSites, setSelectedSites] = useState<SiteDataEntry[]>([]);
  const [selectedWasteTypes, setSelectedWasteTypes] = useState<
    WasteTypeDataEntry[]
  >([]);
  const [selectedStatus, setSelectedStatus] = useState<JobStatusDataEntry[]>(
    []
  );
  const [selectedInvoiceType, setSelectedInvoiceType] = useState<any[]>([]);
  const [selectedDocumentType, setSelectedDocumentType] = useState<any[]>([]);

  useEffect(() => {
    Storage.FILTER_DATA.get().then((filter) => {
      const start = filter.startDate ? new Date(filter.startDate) : null;
      const end = filter.endDate ? new Date(filter.endDate) : null;
      if (start && end) setDateRange([start, end]);
      if (filter.selectedDateIndex !== undefined) {
        setSelectedDate(filter.selectedDateIndex);
      }
    });
  }, []);

  useEffect(() => {
    if (props.customerData && props.customerData.length > 0) {
      Storage.FILTER_DATA.get().then((filter) => {
        const idx = filter.customerIndex >= 0 ? filter.customerIndex : 0;
        setInitialCustomer(props.customerData[idx]);
      });
    }
  }, [props.customerData]);

  useEffect(() => {
    if (props.siteData && props.siteData.length > 0) {
      Storage.FILTER_DATA.get().then((filter) => {
        const siteList: SiteDataEntry[] = [];
        props.siteData?.forEach((site, idx) => {
          if (filter.siteIndex.includes(idx)) {
            siteList.push(site);
          }
        });
        setSelectedSites(siteList);
      });
    }
  }, [props.siteData]);

  useEffect(() => {
    if (props.wasteTypeData && props.wasteTypeData.length > 0) {
      Storage.FILTER_DATA.get().then((filter) => {
        const wasteTypeList: WasteTypeDataEntry[] = [];
        props.wasteTypeData?.forEach((waste, idx) => {
          if (filter.wasteTypeIndex.includes(idx)) {
            wasteTypeList.push(waste);
          }
        });
        setSelectedWasteTypes(wasteTypeList);
      });
    }
  }, [props.wasteTypeData]);

  useEffect(() => {
    if (isClosingDatepicker) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setIsClosingDatepicker(false);
      }, 300);
    }
  }, [isClosingDatepicker]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const changeDisplayedDateRange = useCallback(
    async (dateRange: [Date | null, Date | null], selectedEntry: number) => {
      const start = dateRange[0] ?? undefined;
      const end = dateRange[1] ?? undefined;
      if (start && end) {
        await Storage.FILTER_DATA.set(3, [start, end]);
        Storage.FILTER_DATA.set(4, selectedEntry);
      }
      setDateRange([start, end]);
    },
    []
  );

  const datepickerContainer = useCallback(
    (cc_props: any) => {
      return (
        <div
          className="datepicker-splitter"
          style={{
            marginRight: props.noSideButton && showCalendar ? "12rem" : 0,
          }}
        >
          <div className="shortcut-panel">
            <div className="template-range">
              <div
                className={
                  "range-entry" + (selectedDate === 1 ? " selected" : "")
                }
                onClick={() => {
                  setSelectedDate(1);
                  setShowCalendar(false);
                  const updateRange = thisWeekRangeSelect();
                  changeDisplayedDateRange(updateRange, 1);
                  if (props.onChangeTimeRange)
                    props.onChangeTimeRange(updateRange);
                }}
              >
                Current Week
              </div>
              <div
                className={
                  "range-entry" + (selectedDate === 2 ? " selected" : "")
                }
                onClick={() => {
                  setSelectedDate(2);
                  setShowCalendar(false);
                  const updateRange = previousWeekRangeSelect();
                  changeDisplayedDateRange(updateRange, 2);
                  if (props.onChangeTimeRange)
                    props.onChangeTimeRange(updateRange);
                }}
              >
                Previous Week
              </div>
              <div
                className={
                  "range-entry" + (selectedDate === 3 ? " selected" : "")
                }
                onClick={() => {
                  setSelectedDate(3);
                  setShowCalendar(false);
                  const updateRange = currentMonthRangeSelect();
                  changeDisplayedDateRange(updateRange, 3);
                  if (props.onChangeTimeRange)
                    props.onChangeTimeRange(updateRange);
                }}
              >
                Current Month
              </div>
              <div
                className={
                  "range-entry" + (selectedDate === 4 ? " selected" : "")
                }
                onClick={() => {
                  setSelectedDate(4);
                  setShowCalendar(false);
                  const updateRange = previousMonthRangeSelect();
                  changeDisplayedDateRange(updateRange, 4);
                  if (props.onChangeTimeRange)
                    props.onChangeTimeRange(updateRange);
                }}
              >
                Previous Month
              </div>
              <div
                className={
                  "range-entry" + (selectedDate === 5 ? " selected" : "")
                }
                onClick={() => {
                  setSelectedDate(5);
                  setShowCalendar(false);
                  const updateRange = currentQuarterRangeSelect();
                  changeDisplayedDateRange(updateRange, 5);
                  if (props.onChangeTimeRange)
                    props.onChangeTimeRange(updateRange);
                }}
              >
                Current Quarter
              </div>
              <div
                className={
                  "range-entry" + (selectedDate === 6 ? " selected" : "")
                }
                onClick={() => {
                  setSelectedDate(6);
                  setShowCalendar(false);
                  const updateRange = previousQuarterRangeSelect();
                  changeDisplayedDateRange(updateRange, 6);
                  if (props.onChangeTimeRange)
                    props.onChangeTimeRange(updateRange);
                }}
              >
                Previous Quarter
              </div>
              <div
                className={
                  "range-entry" + (selectedDate === 7 ? " selected" : "")
                }
                onClick={() => {
                  setSelectedDate(7);
                  setShowCalendar(false);
                  const updateRange = currentYearRangeSelect();
                  changeDisplayedDateRange(updateRange, 7);
                  if (props.onChangeTimeRange)
                    props.onChangeTimeRange(updateRange);
                }}
              >
                Current Year
              </div>
              <div
                className={
                  "range-entry" + (selectedDate === 8 ? " selected" : "")
                }
                onClick={() => {
                  setSelectedDate(8);
                  setShowCalendar(false);
                  const updateRange = previousYearRangeSelect();
                  changeDisplayedDateRange(updateRange, 8);
                  if (props.onChangeTimeRange)
                    props.onChangeTimeRange(updateRange);
                }}
              >
                Previous Year
              </div>
              <div
                className={
                  "range-entry" + (selectedDate === 9 ? " selected" : "")
                }
                onClick={() => {
                  setSelectedDate(9);
                  setShowCalendar(false);
                  const updateRange = lastYearSpanRangeSelect();
                  changeDisplayedDateRange(updateRange, 9);
                  if (props.onChangeTimeRange)
                    props.onChangeTimeRange(updateRange);
                }}
              >
                Last 365 Days
              </div>
            </div>
            <div
              className={
                "range-entry" + (selectedDate === 10 ? " selected" : "")
              }
              onClick={() => {
                setSelectedDate(10);
                setShowCalendar(!showCalendar);
                setOpenDropdownId(null);
              }}
            >
              Custom Range
            </div>
          </div>
          {showCalendar && (
            <CalendarContainer
              className={cc_props.className + (props.darkMode ? " dark" : "")}
            >
              <div style={{ position: "relative" }}>{cc_props.children}</div>
            </CalendarContainer>
          )}
        </div>
      );
    },
    [
      changeDisplayedDateRange,
      props,
      selectedDate,
      showCalendar,
      setOpenDropdownId,
    ]
  );

  const handleDropdownToggle = useCallback(
    (dropdownId: string) => {
      if (openDropdownId === dropdownId) {
        if (dropdownId === "customer") setIsClosingDatepicker(true);
        setOpenDropdownId(null);
      } else {
        setOpenDropdownId(dropdownId);
        setShowCalendar(false);
      }
    },
    [openDropdownId]
  );

  const resetAllFilters = useCallback(() => {
    setSiteResetting(true);
    setWasteTypeResetting(true);
    setStatusResetting(true);
    setInvoiceTypeResetting(true);
    setDocumentTypeResetting(true);

    const start = dateRange[0] ?? null;
    const end = dateRange[1] ?? null;
    if (props.onChangeTimeRange) props.onChangeTimeRange([start, end]);
  }, [dateRange, props]);

  return (
    <div
      className={
        "row filter-spacing" +
        (props.darkMode ? " dark" : "") +
        (props.isTippingUser ? " tipping" : "")
      }
    >
      <div className="row">
        <DropdownSelect
          id="customer"
          width={props.width}
          title="customer"
          all="All Customers"
          data={props.customerData}
          preselected={initialCustomer}
          onChange={async (val: CustomerDataEntry) => {
            const idx = props.customerData.findIndex(
              (customer) => customer.value === val.value
            );
            if (idx >= 0) {
              await Storage.FILTER_DATA.set(0, idx);
              await Storage.FILTER_DATA.set(1, []);
              await Storage.FILTER_DATA.set(2, []);
            }
            props.onChangeCustomerData(val);
            resetAllFilters();
          }}
          isOpen={openDropdownId === "customer"}
          onToggle={() => handleDropdownToggle("customer")}
          isTippingUser={props.isTippingUser}
          darkMode={props.darkMode}
        />
        {!props.isTippingUser && props.siteData && (
          <DropdownMultiselect
            id="site"
            width={props.width}
            label="site"
            all="All Sites"
            options={props.siteData.map((site) => ({
              label: site.label,
              value: site.value,
            }))}
            selected={selectedSites}
            onChange={(val: SiteDataEntry[]) => {
              if (props.onChangeSiteData) {
                const idxList: number[] = [];
                props.siteData?.forEach((site, idx) => {
                  if (val.some((v: SiteDataEntry) => v.value === site.value)) {
                    idxList.push(idx);
                  }
                });
                Storage.FILTER_DATA.set(1, idxList);
                setSelectedSites(val);
                props.onChangeSiteData(val);
              }
            }}
            isOpen={openDropdownId === "site"}
            onToggle={() => handleDropdownToggle("site")}
            resetting={siteResetting}
            onResetEnded={() => setSiteResetting(false)}
            darkMode={props.darkMode}
          />
        )}
        {!props.isTippingUser && props.wasteTypeData && (
          <DropdownMultiselect
            id="waste_type"
            width={props.width}
            label="waste_type"
            all="All Waste Types"
            options={props.wasteTypeData.map((wt) => ({
              label: wt.label,
              value: wt.value,
            }))}
            selected={selectedWasteTypes}
            onChange={(val: WasteTypeDataEntry[]) => {
              if (props.onChangeWasteTypeData) {
                const idxList: number[] = [];
                props.wasteTypeData?.forEach((wt, idx) => {
                  if (
                    val.some((v: WasteTypeDataEntry) => v.value === wt.value)
                  ) {
                    idxList.push(idx);
                  }
                });
                Storage.FILTER_DATA.set(2, idxList);
                setSelectedWasteTypes(val);
                props.onChangeWasteTypeData(val);
              }
            }}
            isOpen={openDropdownId === "waste_type"}
            onToggle={() => handleDropdownToggle("waste_type")}
            resetting={wasteTypeResetting}
            onResetEnded={() => setWasteTypeResetting(false)}
            darkMode={props.darkMode}
          />
        )}
        {!props.isTippingUser && props.statusData && (
          <DropdownMultiselect
            id="status"
            width={props.width}
            label="status"
            all="All Statuses"
            options={props.statusData.map((status) => ({
              label: status.label,
              value: status.value,
            }))}
            selected={selectedStatus}
            onChange={(val: JobStatusDataEntry[]) => {
              if (props.onChangeStatusData) {
                setSelectedStatus(val);
                props.onChangeStatusData(val);
              }
            }}
            isOpen={openDropdownId === "status"}
            onToggle={() => handleDropdownToggle("status")}
            resetting={statusResetting}
            onResetEnded={() => setStatusResetting(false)}
            darkMode={props.darkMode}
          />
        )}
        {!props.isTippingUser && props.invoiceTypeData && (
          <DropdownMultiselect
            id="invoice_type"
            width={props.width}
            label="invoice_type"
            all="All Invoice Types"
            options={props.invoiceTypeData.map((it) => ({
              label: it.label,
              value: it.value,
            }))}
            selected={selectedInvoiceType}
            onChange={(val: any[]) => {
              if (props.onChangeInvoiceTypeData) {
                setSelectedInvoiceType(val);
                props.onChangeInvoiceTypeData(val);
              }
            }}
            isOpen={openDropdownId === "invoice_type"}
            onToggle={() => handleDropdownToggle("invoice_type")}
            resetting={invoiceTypeResetting}
            onResetEnded={() => setInvoiceTypeResetting(false)}
            darkMode={props.darkMode}
          />
        )}
        {!props.isTippingUser && props.documentTypeData && (
          <DropdownMultiselect
            id="document_type"
            width={props.width}
            label="document_type"
            all="Document Type..."
            options={props.documentTypeData.map((dt) => ({
              label: dt.label,
              value: dt.value,
            }))}
            selected={selectedDocumentType}
            onChange={(val: any[]) => {
              if (props.onChangeDocumentTypeData) {
                setSelectedDocumentType(val);
                props.onChangeDocumentTypeData(val);
              }
            }}
            isOpen={openDropdownId === "document_type"}
            onToggle={() => handleDropdownToggle("document_type")}
            resetting={documentTypeResetting}
            onResetEnded={() => setDocumentTypeResetting(false)}
            darkMode={props.darkMode}
          />
        )}
      </div>
      {props.displayDateRange && (
        <div
          className={
            "datepicker-selector" +
            (openDropdownId || isClosingDatepicker ? " closed" : "")
          }
          onClick={() => {
            setOpenDropdownId(null);
          }}
        >
          <DatePicker
            showIcon
            selectsRange
            startDate={localStartDate}
            endDate={localEndDate}
            onChange={(update) => {
              changeDisplayedDateRange(update, 10);
              if (update[1] && props.onChangeTimeRange)
                props.onChangeTimeRange(update);
            }}
            onCalendarOpen={() => {
              setOpenDropdownId(null);
            }}
            icon={
              <FontAwesomeIcon className="calendar-icon" icon={faCalendar} />
            }
            calendarContainer={datepickerContainer}
            popperPlacement="bottom-start"
            dateFormat="dd-MM-yyyy"
          />
        </div>
      )}
    </div>
  );
});

export default FilterRow;
